import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, 
  Smile,
  MessageCircle,
  X,
  Loader2,
  Trash2,
  ChevronDown,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { getImageUrl } from '../../utils/imageUtils';

const API = process.env.REACT_APP_BACKEND_URL;

// Message type styling
const MESSAGE_TYPE_STYLES = {
  text: '',
  motivation: 'bg-blue-500/10 border-l-4 border-blue-500',
  celebration: 'bg-yellow-500/10 border-l-4 border-yellow-500',
  progress: 'bg-green-500/10 border-l-4 border-green-500'
};

// Available reactions
const REACTIONS = ['💪', '🔥', '👏', '❤️', '🎉', '⭐'];

export const TeamChat = ({ teamId, api, currentUserId, teamName }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showQuickMessages, setShowQuickMessages] = useState(false);
  const [quickMessages, setQuickMessages] = useState({});
  const [showReactions, setShowReactions] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      const res = await api().get(`/team-chat/${teamId}/messages`);
      setMessages(res.data.messages || []);
    } catch (err) {
      if (err.response?.status !== 403) {
        toast.error('Failed to load messages');
      }
    }
    setLoading(false);
  }, [api, teamId]);

  // Fetch quick messages
  const fetchQuickMessages = useCallback(async () => {
    try {
      const res = await api().get('/team-chat/quick-messages');
      setQuickMessages(res.data.quick_messages || {});
    } catch {
      // silent
    }
  }, [api]);

  useEffect(() => {
    fetchMessages();
    fetchQuickMessages();
    
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [fetchMessages, fetchQuickMessages]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const res = await api().post(`/team-chat/${teamId}/messages`, {
        content: newMessage,
        message_type: 'text'
      });
      setMessages(prev => [...prev, res.data.chat_message]);
      setNewMessage('');
    } catch (err) {
      toast.error('Failed to send message');
    }
    setSending(false);
  };

  const handleQuickMessage = async (category, index) => {
    try {
      const res = await api().post(`/team-chat/${teamId}/quick-message?category=${category}&index=${index}`);
      setMessages(prev => [...prev, res.data.chat_message]);
      setShowQuickMessages(false);
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      await api().post(`/team-chat/${teamId}/messages/${messageId}/react`, { emoji });
      // Refresh messages to get updated reactions
      fetchMessages();
      setShowReactions(null);
    } catch (err) {
      toast.error('Failed to add reaction');
    }
  };

  const handleDelete = async (messageId) => {
    try {
      await api().delete(`/team-chat/${teamId}/messages/${messageId}`);
      setMessages(prev => prev.map(m => 
        m.id === messageId 
          ? { ...m, content: '[Message deleted]', is_deleted: true }
          : m
      ));
    } catch (err) {
      toast.error('Failed to delete message');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-card border border-border flex flex-col h-[500px]">
      {/* Chat Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-primary" />
          <h3 className="font-heading text-lg text-white uppercase tracking-wider">Team Chat</h3>
          {teamName && <Badge className="bg-primary/20 text-primary">{teamName}</Badge>}
        </div>
        <span className="text-xs text-muted-foreground">{messages.length} messages</span>
      </div>

      {/* Messages Container */}
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <MessageCircle className="h-12 w-12 mb-3 opacity-50" />
            <p>No messages yet</p>
            <p className="text-sm">Start the conversation with your team!</p>
          </div>
        ) : (
          messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isOwn={message.user_id === currentUserId}
              onReact={(emoji) => handleReaction(message.id, emoji)}
              onDelete={() => handleDelete(message.id)}
              showReactions={showReactions === message.id}
              setShowReactions={setShowReactions}
              formatTime={formatTime}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Messages Panel */}
      <AnimatePresence>
        {showQuickMessages && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-border overflow-hidden"
          >
            <div className="p-3 space-y-3 max-h-48 overflow-y-auto">
              {Object.entries(quickMessages).map(([category, msgs]) => (
                <div key={category}>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 capitalize">
                    {category}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {msgs.map((msg) => (
                      <Button
                        key={`${category}-${msg}`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickMessage(category, msgs.indexOf(msg))}
                        className="text-xs h-auto py-1 px-2"
                      >
                        {msg}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input Area */}
      <div className="p-4 border-t border-border">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowQuickMessages(!showQuickMessages)}
            className={showQuickMessages ? 'bg-primary/20 text-primary' : ''}
            title="Quick messages"
          >
            <Sparkles className="h-4 w-4" />
          </Button>
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Type a message..."
            className="flex-1 bg-black border-border text-white"
            disabled={sending}
          />
          <Button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            className="bg-primary text-black"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

const MessageBubble = ({ 
  message, 
  isOwn, 
  onReact, 
  onDelete, 
  showReactions, 
  setShowReactions,
  formatTime 
}) => {
  const typeStyle = MESSAGE_TYPE_STYLES[message.message_type] || '';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`max-w-[80%] ${isOwn ? 'items-end' : 'items-start'}`}>
        {/* User info (for others' messages) */}
        {!isOwn && (
          <div className="flex items-center gap-2 mb-1">
            <Avatar className="h-6 w-6">
              <AvatarImage src={getImageUrl(message.user?.profile_picture)} />
              <AvatarFallback className="bg-primary/20 text-primary text-xs">
                {message.user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs text-muted-foreground">{message.user?.name}</span>
          </div>
        )}
        
        {/* Message bubble */}
        <div 
          className={`relative group px-4 py-2 rounded-lg ${typeStyle} ${
            isOwn 
              ? 'bg-primary text-black rounded-br-none' 
              : 'bg-white/10 text-white rounded-bl-none'
          } ${message.is_deleted ? 'opacity-50 italic' : ''}`}
        >
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          
          {/* Timestamp */}
          <span className={`text-xs mt-1 block ${isOwn ? 'text-black/60' : 'text-white/60'}`}>
            {formatTime(message.created_at)}
          </span>
          
          {/* Actions (show on hover) */}
          {!message.is_deleted && (
            <div className={`absolute ${isOwn ? 'left-0 -translate-x-full' : 'right-0 translate-x-full'} top-0 px-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1`}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowReactions(showReactions ? null : message.id)}
                className="h-6 w-6 p-0 text-muted-foreground hover:text-white"
              >
                <Smile className="h-4 w-4" />
              </Button>
              {isOwn && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
          
          {/* Reactions picker */}
          <AnimatePresence>
            {showReactions && (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className={`absolute ${isOwn ? 'right-0' : 'left-0'} -bottom-10 bg-card border border-border rounded-lg p-1 flex gap-1 z-10`}
              >
                {REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => onReact(emoji)}
                    className={`text-lg hover:scale-125 transition-transform p-1 ${
                      message.user_reactions?.includes(emoji) ? 'bg-primary/20 rounded' : ''
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Reactions display */}
        {Object.keys(message.reactions || {}).length > 0 && (
          <div className="flex gap-1 mt-1 flex-wrap">
            {Object.entries(message.reactions).map(([emoji, count]) => (
              <button
                key={emoji}
                onClick={() => onReact(emoji)}
                className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                  message.user_reactions?.includes(emoji)
                    ? 'bg-primary/20 text-primary'
                    : 'bg-white/10 text-white/70'
                }`}
              >
                <span>{emoji}</span>
                <span>{count}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export const TeamChatButton = ({ teamId, api, onOpen }) => {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await api().get(`/team-chat/${teamId}/unread-count`);
        setUnreadCount(res.data.unread_count || 0);
      } catch (err) {
        // Ignore errors
      }
    };
    
    fetchUnread();
    const interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, [api, teamId]);

  return (
    <Button
      onClick={onOpen}
      variant="outline"
      className="relative"
    >
      <MessageCircle className="h-4 w-4 mr-2" />
      Team Chat
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </Button>
  );
};
